/**
 * The one chain that turns a traveller's selection into what they owe.
 *
 * Season → active package → seasonal prices → categories → party total. It was
 * inline in `_booking-sidebar.tsx`, which was fine while the rail was the only
 * thing that showed a price. The pinned mobile bar now shows the same figure,
 * and it has to show it from the FIRST PAINT — while on a phone the rail is
 * deliberately not mounted yet (it waits for `sidebarNear`, to keep its
 * hydration out of the LCP window). So the bar cannot ask the rail; it has to
 * ask the same function the rail asks.
 *
 * That is the whole reason this file exists, and the reason it is pure: no
 * fetch, no hooks, no React. Calling it twice costs arithmetic over arrays the
 * page already holds — cheap enough to be the honest answer, where a second
 * hand-rolled copy of the maths in the bar would be the start of the next
 * pricing bug. Every money defect this page has had was two places deriving one
 * number and drifting (see this folder's CLAUDE.md, invariants 1 and 2).
 */
import type { ExperienceDto } from "@tripcart/shared";
import {
  liveOriginalTotalMinor,
  liveTotalMinor,
} from "@tripcart/shared/pricing/booking-math";

import type { TravelersState } from "@/lib/cart/types";
import { localDateStr } from "@tripcart/shared";

import { sumPax } from "./_capacity";
import { resolveActivePackage } from "./_booking-config";
import { showsPartyTotal, type PartyTotals } from "./_price-presentation";
import { applySeasonToPackage } from "./_season-pricing";

/** The season shape as the experience DTO carries it — NOT `@tripcart/shared`'s
 *  `SeasonDto`, which is a different, flatter record (`startDate`/`endDate`).
 *  Mixing the two is what made the rail's seasons prop stop compiling. */
export type TourSeason = NonNullable<ExperienceDto["seasons"]>[number];

/** The priced categories of the active package, seasonal prices applied. Taken
 *  from `applySeasonToPackage`'s own return rather than re-declared: the pricing
 *  maths needs every field on them, where `PaxCategory` is the narrow
 *  capacity-only view `_capacity.ts` works in. */
export type PricedCategory = NonNullable<
  ReturnType<typeof applySeasonToPackage>
>["categories"][number];

/**
 * The season covering `date`, or null. An open-ended season matches on the side
 * it leaves open. Compared as ISO day strings, never as `Date`s: a `Date`
 * carries a time and a zone, and both make "is this day inside the season"
 * answer differently either side of midnight.
 *
 * KNOWN DIVERGENCE, moved here unchanged rather than introduced: this folder's
 * CLAUDE.md invariant 3 says the season comes from the selected departure's
 * `seasonId` and is "never a front-end first-match scan over `seasons`" — and a
 * first-match scan is exactly what this is. Consolidating it here is still a
 * net win, because the rail and the pinned bar can no longer disagree with each
 * OTHER, which is the failure mode this page has actually shipped. But both can
 * still disagree with the API, and an overlapping-season configuration is where
 * that would show. Do not read this as the blessed pattern; the fix is to
 * thread `departure.seasonId` down, which is a DTO-shaped change, not a move.
 */
export function resolveActiveSeason(
  seasons: TourSeason[] | undefined,
  date: Date | undefined,
): TourSeason | null {
  if (!seasons?.length || !date) return null;
  const iso = localDateStr(date);
  return (
    seasons.find((s) => {
      if (s.startsOn && iso < s.startsOn) return false;
      if (s.endsOn && iso > s.endsOn) return false;
      return true;
    }) ?? null
  );
}

export interface BookingPricing {
  activeSeason: TourSeason | null;
  /** The chosen package BEFORE seasonal prices are applied. Capacity is a
   *  property of the package itself, not of the season's price list, so the
   *  picker cap reads this one — `resolveGlobalCapacityCap(rawPkg, …)`. */
  rawPkg: ReturnType<typeof resolveActivePackage>;
  /** The chosen package with the active season's prices applied, or null. */
  activePkg: ReturnType<typeof applySeasonToPackage> | null;
  categories: PricedCategory[];
  /** Null when there is no priced category to total — the caller shows a
   *  from-price rather than announcing a total of zero. */
  totals: PartyTotals | null;
}

/**
 * Resolve everything price-related for the current selection.
 *
 * `selectedDate` and `selectedPackageId` are both allowed to be undefined: that
 * is the state the page is in before the traveller has chosen anything, and it
 * still has a real answer — the default package at base-season prices, which is
 * exactly the figure the rail shows under "Starting from".
 */
export function resolveBookingPricing({
  packages,
  seasons,
  selectedDate,
  selectedPackageId,
  travelers,
}: {
  packages: Parameters<typeof resolveActivePackage>[0];
  seasons: TourSeason[] | undefined;
  selectedDate: Date | undefined;
  selectedPackageId: string | undefined;
  travelers: TravelersState;
}): BookingPricing {
  const activeSeason = resolveActiveSeason(seasons, selectedDate);
  const rawPkg = resolveActivePackage(packages, selectedPackageId);
  const activePkg = rawPkg ? applySeasonToPackage(rawPkg, activeSeason) : null;
  const categories = activePkg?.categories ?? [];

  if (categories.length === 0) {
    return { activeSeason, rawPkg, activePkg, categories, totals: null };
  }

  return {
    activeSeason,
    rawPkg,
    activePkg,
    categories,
    totals: {
      // MINOR UNITS throughout — the page's money invariant. Never divide by
      // 100 here or anywhere downstream; formatting is the view's job.
      totalMinor: liveTotalMinor(categories, travelers),
      originalTotalMinor: liveOriginalTotalMinor(categories, travelers),
      // Heads, via `sumPax` — a Cabin row of four is four travellers, not one.
      headCount: sumPax(categories, travelers),
      isPartyTotal: showsPartyTotal(categories, travelers),
    },
  };
}
